import { useApolloClient, useQuery } from '@apollo/client';
import { Clock3, Printer, RefreshCw, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CASHIER_SHIFT_SALE_HISTORY_QUERY,
  KIT_BREAKDOWN_LINES_BY_CODE_QUERY,
  KIT_BREAKDOWN_LINES_QUERY,
} from '@/modules/cashier-pos/cashier-pos.gql';
import {
  collectKitBreakdownTargets,
  customerLabel,
  dateTimeLabel,
  groupItemsByKit,
  kitBreakdownDisplayRows,
  kitComponentLabel,
  petLabel,
  printSaleTicket,
  ticketLabel,
  timeLabel,
  type OrderCustomerSnapshot,
  type OrderPetSnapshot,
  type SaleOrder,
  type TicketPaymentMethod,
} from '@/modules/cashier-pos/sale-order-shared';
import { formatCentsString } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { useActiveShiftStore } from '@/stores/activeShiftStore';
import { useAuthStore } from '@/stores/authStore';

interface CashierShiftSaleHistoryEntry {
  sale_order_id: string;
  sale_folio_number: string | null;
  ticket_folio_number: string | null;
  paid_at: string | null;
  total_cents: string;
  tutor_name: string | null;
  pet_name: string | null;
  customer_snapshot_json?: OrderCustomerSnapshot | string | null;
  pet_snapshot_json?: OrderPetSnapshot | string | null;
  payment_methods: TicketPaymentMethod[];
  payment_method_summary: string | null;
  consulting_room_name?: string | null;
  sale_order: SaleOrder;
}

interface CashierShiftSaleHistoryResponse {
  cashierShiftSaleHistory: CashierShiftSaleHistoryEntry[];
}

interface ShiftHistoryModalProps {
  on_close: () => void;
}

/** Single-station equivalent of the shift-sale-history table + reprint detail that
 *  lives at the bottom of `CashierPosPage.tsx` — same query, moved behind a button to
 *  free up screen space for the active sale. */
export function ShiftHistoryModal({ on_close }: ShiftHistoryModalProps): JSX.Element {
  const { t } = useTranslation();
  const apollo = useApolloClient();
  const cashier_name = useAuthStore((s) => s.user?.full_name ?? 'Caja');
  const active_shift_id = useActiveShiftStore((s) => s.shift_id);
  const [kit_breakdowns, setKitBreakdowns] = useState<Record<string, string[]>>({});
  const [detail, setDetail] = useState<CashierShiftSaleHistoryEntry | null>(null);

  const { data, refetch } = useQuery<CashierShiftSaleHistoryResponse>(
    CASHIER_SHIFT_SALE_HISTORY_QUERY,
    {
      variables: { cash_shift_id: active_shift_id ?? '', offset: 0, limit: 25 },
      skip: !active_shift_id,
      fetchPolicy: 'cache-and-network',
    },
  );
  const entries = data?.cashierShiftSaleHistory ?? [];

  const loadKitBreakdowns = async (
    items: SaleOrder['items'],
  ): Promise<Record<string, string[]>> => {
    const targets = collectKitBreakdownTargets(items);
    const missing = targets.filter(
      (target) => !Object.prototype.hasOwnProperty.call(kit_breakdowns, target.key),
    );
    if (missing.length === 0) return kit_breakdowns;
    const fetched: Record<string, string[]> = {};
    for (const target of missing) {
      try {
        if (target.kind === 'id') {
          const res = await apollo.query<{ kitBreakdownLines: string[] }>({
            query: KIT_BREAKDOWN_LINES_QUERY,
            variables: { kit_id: target.value },
            fetchPolicy: 'network-only',
          });
          fetched[target.key] = res.data?.kitBreakdownLines ?? [];
          continue;
        }
        const res = await apollo.query<{ kitBreakdownLinesByCode: string[] }>({
          query: KIT_BREAKDOWN_LINES_BY_CODE_QUERY,
          variables: { kit_code: target.value },
          fetchPolicy: 'network-only',
        });
        fetched[target.key] = res.data?.kitBreakdownLinesByCode ?? [];
      } catch {
        fetched[target.key] = [];
      }
    }
    const merged = { ...kit_breakdowns, ...fetched };
    setKitBreakdowns(merged);
    return merged;
  };

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && on_close()}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              <DialogTitle>
                {t('cashier_pos.shift_history_title')} - {entries.length}
              </DialogTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (active_shift_id) void refetch();
                }}
                disabled={!active_shift_id}
                aria-label={t('cashier_pos.refresh_history')}
              >
                <RefreshCw className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </DialogHeader>
          {entries.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center text-sm">
              {t('cashier_pos.shift_history_empty')}
            </p>
          ) : (
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left">{t('cashier_pos.history_time')}</th>
                    <th className="px-3 py-2 text-left">{t('cashier_pos.history_ticket')}</th>
                    <th className="px-3 py-2 text-right">{t('cashier_pos.history_amount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-border divide-y">
                  {entries.map((entry) => (
                    <tr
                      key={entry.sale_order_id}
                      className="hover:bg-accent/10 cursor-pointer"
                      onClick={() => setDetail(entry)}
                    >
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-2">
                          <Clock3 className="text-muted-foreground h-4 w-4" aria-hidden />
                          {timeLabel(entry.paid_at)}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono">
                        {ticketLabel(entry.ticket_folio_number, entry.sale_folio_number)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {formatCentsString(entry.total_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {detail ? (
        <div className="bg-background/80 fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <Card className="max-h-[90vh] w-full max-w-2xl overflow-auto">
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">
                  {t('cashier_pos.history_detail_title')}{' '}
                  {ticketLabel(detail.ticket_folio_number, detail.sale_folio_number)}
                </CardTitle>
                <p className="text-muted-foreground text-xs">
                  {dateTimeLabel(detail.paid_at)} -{' '}
                  {detail.consulting_room_name ?? t('cashier_pos.no_consulting_room')}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() =>
                    void loadKitBreakdowns(detail.sale_order.items).then((resolved) =>
                      printSaleTicket(detail.sale_order, {
                        mode: 'reprint',
                        cashier_name,
                        ticket_number: ticketLabel(detail.ticket_folio_number, detail.sale_folio_number),
                        kit_breakdowns: resolved,
                        payment_methods: detail.payment_methods,
                        payment_method_summary: detail.payment_method_summary,
                      }),
                    )
                  }
                  aria-label={t('cashier_pos.print_ticket')}
                >
                  <Printer className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setDetail(null)}
                  aria-label={t('common.cancel')}
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 text-sm md:grid-cols-2">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Tutor</p>
                  <p className="font-medium">
                    {detail.tutor_name ?? customerLabel(detail.customer_snapshot_json)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">Mascota</p>
                  <p className="font-medium">{detail.pet_name ?? petLabel(detail.pet_snapshot_json)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('cashier_pos.payment_title')}
                  </p>
                  <p className="font-medium">{detail.payment_method_summary ?? 'Pago registrado'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('cashier_pos.total')}
                  </p>
                  <p className="font-mono font-medium">{formatCentsString(detail.total_cents)}</p>
                </div>
              </div>
              <ul className="divide-border divide-y rounded-md border text-sm">
                {groupItemsByKit(detail.sale_order.items).map((group, index) => (
                  <li key={`history-kit-group-${index}`} className="px-3 py-2">
                    {group.kit_label ? (
                      <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                        Kit: {group.kit_label}
                      </p>
                    ) : null}
                    <ul className="space-y-1">
                      {group.items.map((item) => (
                        <li key={item.id} className="flex items-center justify-between gap-3">
                          <span>
                            <span className="text-muted-foreground font-mono text-xs">
                              {item.item_code}
                            </span>{' '}
                            {group.kit_label ? kitComponentLabel(item.item_name) : item.item_name}
                            {(() => {
                              const lines = kitBreakdownDisplayRows(item, kit_breakdowns);
                              return lines.length > 0 ? (
                                <span className="text-muted-foreground mt-1 block text-xs">
                                  {lines.join(' | ')}
                                </span>
                              ) : null;
                            })()}
                          </span>
                          <span className="font-mono">{formatCentsString(item.total_cents)}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </>
  );
}
